import { HTMLAttributes, RefObject, useState, useEffect, useMemo, useRef } from 'react'

import type { TrackIdentifier } from '@livekit/components-core'
import { isTrackReference } from '@livekit/components-core'
import { setupMediaTrack, log, isLocal, getTrackByIdentifier } from '@livekit/components-core'

import { mergeProps } from '@/src/utils'

/** @public */
export interface UseMediaTrackOptions {
  element?: RefObject<HTMLMediaElement> | null
  props?: HTMLAttributes<HTMLVideoElement | HTMLAudioElement>
}

/**
 * @internal
 */
export function useMediaTrackBySourceOrName(
  observerOptions: TrackIdentifier,
  options: UseMediaTrackOptions = {},
  isPiP?: boolean,
) {
  const [publication, setPublication] = useState(getTrackByIdentifier(observerOptions))

  const [isMuted, setMuted] = useState(publication?.isMuted)
  const [isSubscribed, setSubscribed] = useState(publication?.isSubscribed)

  const [track, setTrack] = useState(publication?.track)
  const [bgEffect, setBgEffect] = useState<string>('')
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape')
  const previousElement = useRef<HTMLMediaElement | undefined | null>()

  const { className, trackObserver } = useMemo(
    () => setupMediaTrack(observerOptions),
    [
      observerOptions.participant.sid ?? observerOptions.participant.identity,
      observerOptions.source,
      isTrackReference(observerOptions) && observerOptions.publication.trackSid,
    ],
  )

  useEffect(() => {
    const updateFromLocalStorage = () => {
      const userChoices = localStorage.getItem('lk-user-choices')
      if (userChoices) {
        setBgEffect(JSON.parse(userChoices)?.currentBgEffect?.type)
      }
    }
    updateFromLocalStorage()
    window.addEventListener('storage', updateFromLocalStorage)

    return () => {
      window.removeEventListener('storage', updateFromLocalStorage)
    }
  }, [])

  useEffect(() => {
    const subscription = trackObserver.subscribe((publication) => {
      log.debug('update track', publication)
      setPublication(publication)
      setMuted(publication?.isMuted)
      setSubscribed(publication?.isSubscribed)
      setTrack(publication?.track)
    })
    return () => subscription?.unsubscribe()
  }, [trackObserver])

  useEffect(() => {
    if (track) {
      if (previousElement.current) {
        track?.detach(previousElement.current)
      }

      if (
        options.element?.current &&
        !(isLocal(observerOptions.participant) && track?.kind === 'audio')
      ) {
        track?.attach(options.element.current)
      }
    }

    previousElement.current = options.element?.current

    return () => {
      if (previousElement.current) {
        track?.detach(previousElement.current)
      }
    }
  }, [track, options.element, observerOptions.participant])

  useEffect(() => {
    // Set the orientation of the video track.
    // TODO: This does not handle changes in orientation after a track got published (e.g when rotating a phone camera from portrait to landscape).
    if (
      typeof publication?.dimensions?.width === 'number' &&
      typeof publication?.dimensions?.height === 'number'
    ) {
      const orientation_ =
        publication.dimensions.width > publication.dimensions.height ? 'landscape' : 'portrait'
      setOrientation(orientation_)
    }
  }, [publication])

  return {
    publication,
    isMuted,
    isSubscribed,
    track,
    elementProps: mergeProps(options.props, {
      className: `${className}${isPiP ? ' pip-track' : ''}`,
      'data-lk-local-participant': observerOptions.participant.isLocal,
      'data-lk-bg-effect': bgEffect,
      'data-lk-source': publication?.source,
      ...(publication?.kind === 'video' && { 'data-lk-orientation': orientation }),
    }),
  }
}
